import qs from 'qs'
import cookies from 'js-cookie'
import config from '../config'

export default (context, inject) => {
  const { $axios } = context

  function setCsrfHeader(token) {
    $axios.setHeader('X-CSRFToken', token, ['post', 'put', 'delete', 'patch'])
  }

  function saveCsrfToken(token) {
    cookies.set('csrf-token', token, {
      expires: (1 / 24) * 8 // 8 hours
    })
  }

  async function publicCsrfWrapper(fn) {
    const { data: token } = await $axios({
      method: 'GET',
      url: '/csrf/'
    })

    setCsrfHeader(token.csrfToken)
    return fn()
  }

  async function csrfWrapper(fn) {
    const csrfToken = cookies.get('csrf-token')

    if (typeof csrfToken === 'undefined') {
      const { data: token } = await $axios({
        method: 'GET',
        url: '/csrf/'
      })

      saveCsrfToken(token.csrfToken)
      setCsrfHeader(token.csrfToken)
    } else {
      setCsrfHeader(csrfToken)
    }

    return fn()
  }

  inject('api', {
    ping: () => {
      return $axios({
        method: 'GET',
        url: '/ping/'
      })
    },

    refreshCsrfToken: async () => {
      const { data: token } = await $axios({
        method: 'GET',
        url: '/csrf/'
      })

      saveCsrfToken(token.csrfToken)
    },

    getMe: () => {
      return $axios({
        method: 'GET',
        url: '/profile/me/'
      })
    },

    patchState: (state) => {
      return publicCsrfWrapper(() => {
        $axios({
          method: 'PATCH',
          url: '/profile/states/',
          headers: {
            'content-type': 'application/x-www-form-urlencoded'
          },
          data: qs.stringify({
            state
          })
        })
      })
    },

    patchOptions: (options) => {
      return publicCsrfWrapper(() => {
        $axios({
          method: 'PATCH',
          url: '/profile/options/',
          headers: {
            'content-type': 'application/x-www-form-urlencoded'
          },
          data: qs.stringify(options)
        })
      })
    },

    // TODO: remove
    changePassword: ({ currentPassword, newPassword, confirmNewPassword }) => {
      return csrfWrapper(() =>
        $axios({
          method: 'PUT',
          url: '/profile/password/',
          headers: {
            'content-type': 'application/x-www-form-urlencoded'
          },
          data: qs.stringify({
            current_password: currentPassword,
            new_password: newPassword,
            confirm_new_password: confirmNewPassword
          })
        })
      )
    },

    // keep it
    signOut: () => {
      return csrfWrapper(() =>
        $axios({
          method: 'POST',
          url: '/logout/'
        })
      )
    },

    getDisciplines: (surveyId, q) => {
      return $axios({
        method: 'GET',
        url: `/surveys/${surveyId}/disciplines/search/?q=${q}`
      })
    },

    getSkills: ({ query, excludeSkillIds }) => {
      return csrfWrapper(() => {
        return $axios({
          method: 'POST',
          url: `/skills/search/`,
          headers: {
            'content-type': 'application/x-www-form-urlencoded'
          },
          data: qs.stringify({
            q: query,
            excludes: excludeSkillIds.join(',')
          })
        })
      })
    },

    getTopDisciplines: (surveyId) => {
      return $axios({
        method: 'GET',
        url: `/surveys/${surveyId}/disciplines/`
      })
    },

    getDiscipline: ({ surveyId, disciplineCode }) => {
      return $axios({
        method: 'GET',
        url: `/surveys/${surveyId}/disciplines/${disciplineCode}/`
      })
    },

    getDisciplineCareerLevel: ({
      surveyId,
      disciplineCode,
      careerLevelCode
    }) => {
      return $axios({
        method: 'GET',
        url: `/surveys/${surveyId}/disciplines/${disciplineCode}/career_levels/${careerLevelCode}/`
      })
    },

    compute: ({ surveyId, disciplineCode, careerLevelCode, skillIds = [] }) => {
      return csrfWrapper(() => {
        return $axios({
          method: 'POST',
          url: `/surveys/${surveyId}/disciplines/${disciplineCode}/career_levels/${careerLevelCode}/compute/`,
          headers: {
            'content-type': 'application/x-www-form-urlencoded'
          },
          data: qs.stringify({
            skillIds: skillIds.join(',')
          })
        })
      })
    },

    export: ({
      surveyId,
      disciplineCode,
      careerLevelCode,
      locale,
      skillIds = []
    }) => {
      return csrfWrapper(() => {
        return $axios({
          method: 'POST',
          url: `/surveys/${surveyId}/disciplines/${disciplineCode}/career_levels/${careerLevelCode}/export/`,
          headers: {
            'content-type': 'application/x-www-form-urlencoded'
          },
          responseType: 'blob',
          data: qs.stringify({
            skillIds: skillIds.join(','),
            locale
          })
        })
      })
    },

    bookmarkDiscipline: ({ surveyId, disciplineCode, careerLevelCode }) => {
      return csrfWrapper(() => {
        return $axios({
          method: 'POST',
          url: `/surveys/${surveyId}/disciplines/${disciplineCode}/career_levels/${careerLevelCode}/bookmark/`
        })
      })
    },

    deleteBookmarkedDiscipline: ({
      surveyId,
      disciplineCode,
      careerLevelCode
    }) => {
      return csrfWrapper(() => {
        return $axios({
          method: 'DELETE',
          url: `/surveys/${surveyId}/disciplines/${disciplineCode}/career_levels/${careerLevelCode}/bookmark/`
        })
      })
    },

    getBookmarkedDisciplines: (surveyId) => {
      return $axios({
        method: 'GET',
        url: `/surveys/${surveyId}/disciplines/bookmarked/`
      })
    },

    getRecentSearches: (surveyId) => {
      return $axios({
        method: 'GET',
        url: `/surveys/${surveyId}/disciplines/recent_search/`
      })
    },

    getPdfGuide: () => {
      return $axios({
        method: 'GET',
        url: `/guide/?locale=${cookies.get(config.COOKIE_NAME__LOCALE) ||
          config.defaultLocale}`,
        responseType: 'blob'
      })
    }
  })
}
